import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { EQUIPES_COM_AVISO } from './permissoesSolicitacao';

/**
 * Selo "Visualizado" / "Não visualizado" (#6552): quem vai atender já viu o aviso na tela da
 * solicitação? Só as equipes com aviso (EQUIPES_COM_AVISO), e só quando houve aviso. As linhas da lista
 * pedem juntas: um cache por solicitação e uma consulta só a cada leva.
 */

type Visto = { visto_em: string | null; quem: string | null } | null;

const cache = new Map<number, Visto>();
const ouvintes = new Map<number, Set<() => void>>();
let fila = new Set<number>();
let agendado: ReturnType<typeof setTimeout> | null = null;

async function buscarLeva() {
  const ids = [...fila]; fila = new Set(); agendado = null;
  if (ids.length === 0) return;
  const { data } = await supabase.from('avisos_solicitacao')
    .select('tarefa_id, visto_em, user_id')
    .eq('tipo', 'recebida').in('tarefa_id', ids).order('criado_em', { ascending: false });
  const linhas = (data ?? []) as { tarefa_id: number; visto_em: string | null; user_id: string }[];
  const pessoas = [...new Set(linhas.map(l => l.user_id))];
  const { data: perfis } = pessoas.length
    ? await supabase.from('profiles').select('id, full_name').in('id', pessoas)
    : { data: [] as { id: string; full_name: string | null }[] };
  const nome = new Map((perfis ?? []).map(p => [p.id, p.full_name]));
  const porTarefa = new Map<number, Visto>();
  for (const a of linhas) {
    // O aviso mais novo manda (troca de responsável gera um aviso novo).
    if (!porTarefa.has(a.tarefa_id)) porTarefa.set(a.tarefa_id, { visto_em: a.visto_em, quem: nome.get(a.user_id) ?? null });
  }
  for (const id of ids) {
    cache.set(id, porTarefa.get(id) ?? null);
    ouvintes.get(id)?.forEach(f => f());
  }
}

function pedir(id: number) {
  fila.add(id);
  if (!agendado) agendado = setTimeout(buscarLeva, 50);
}

/** Esquece o que foi lido (ex.: depois de trocar o responsável). */
export const recarregarVistos = () => { cache.clear(); ouvintes.forEach((_, id) => pedir(id)); };

export const SeloVisualizado: React.FC<{ tarefa: { id: string | number; departamento_solicitacao?: string | null }; className?: string }> = ({ tarefa, className = '' }) => {
  const id = Number(tarefa.id);
  const comAviso = EQUIPES_COM_AVISO.includes(tarefa.departamento_solicitacao || '');
  const [, forcar] = useState(0);

  useEffect(() => {
    if (!comAviso) return;
    const f = () => forcar(n => n + 1);
    if (!ouvintes.has(id)) ouvintes.set(id, new Set());
    ouvintes.get(id)!.add(f);
    if (!cache.has(id)) pedir(id);
    return () => { ouvintes.get(id)?.delete(f); };
  }, [id, comAviso]);

  const v = cache.get(id);
  if (!comAviso || !v) return null;
  const quando = v.visto_em ? new Date(v.visto_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null;
  return v.visto_em ? (
    <span title={`Aviso visto${v.quem ? ` por ${v.quem}` : ''} em ${quando}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold whitespace-nowrap normal-case tracking-normal bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 ${className}`}>
      <Eye size={11} aria-hidden="true" /> Visualizado
    </span>
  ) : (
    <span title={`${v.quem ? `${v.quem} ainda` : 'Ainda'} não viu o aviso`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold whitespace-nowrap normal-case tracking-normal bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 ${className}`}>
      <EyeOff size={11} aria-hidden="true" /> Não visualizado
    </span>
  );
};
